/**
 * CustomizePanel.jsx - "Customize view", round 4 (Sharif, 10/9/26: "revamp
 * the whole customize UI ... the most friendly UI interaction, and full
 * customizability"). A panel docked at the right of the screen, so the table
 * stays in sight and every change previews on it live. Five tabs - Columns,
 * Highlights, Appearance, Rows & totals, Sharing (parts in customize/)
 * (plus Group rows on a table that can group by main project, 10/14/26) - each
 * with its own Reset; the footer says when the view has unsaved changes and
 * offers Save (your own view), Save as new, Revert and Reset everything.
 * Escape or Done closes it. Every setting is part of the view (viewConfig.js).
 */
import { useEffect, useRef, useState } from 'react';
import { Button } from '../ui';
import ColumnsSection from './customize/ColumnsSection';
import HighlightsSection from './customize/HighlightsSection';
import { AppearanceSection, RowsSection, SharingSection } from './customize/LookSections';
import GroupRowsSection from './customize/GroupRowsSection';

const BASE_TABS = [
  ['columns', 'Columns'],
  ['highlights', 'Highlights'],
  ['appearance', 'Appearance'],
  ['rows', 'Rows & totals'],
  ['sharing', 'Sharing'],
];

export default function CustomizePanel({ view, defs, onClose, onShare, initialTab = 'columns', hasGroups = false }) {
  // Group rows: on the tables that group by main project (not GLOBAL KSA), grouped yet or not
  const TABS = view.grouping ? [...BASE_TABS.slice(0, 4), ['groups', 'Group rows'], BASE_TABS[4]] : BASE_TABS;
  const [tab, setTab] = useState(initialTab);
  const [naming, setNaming] = useState(null); // the "Save as new" name, while typing it
  const panelRef = useRef(null);
  const tabRefs = useRef({});
  const readOnly = !!(view.active && view.active.mine === false);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !e.defaultPrevented) onClose();
    };
    document.addEventListener('keydown', onKey);
    if (panelRef.current) panelRef.current.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // ← → between tabs, as a tab list should
  const onTabKey = (e) => {
    const i = TABS.findIndex(([k]) => k === tab);
    const next =
      e.key === 'ArrowRight' ? TABS[(i + 1) % TABS.length] : e.key === 'ArrowLeft' ? TABS[(i - 1 + TABS.length) % TABS.length] : null;
    if (!next) return;
    e.preventDefault();
    setTab(next[0]);
    if (tabRefs.current[next[0]]) tabRefs.current[next[0]].focus();
  };

  const saveNew = async (e) => {
    e.preventDefault();
    const n = (naming || '').trim();
    if (n && (await view.saveAs(n, false))) setNaming(null);
  };

  return (
    <aside
      ref={panelRef}
      tabIndex={-1}
      role="dialog"
      aria-label="Customize view"
      className="go-panel-in fixed top-0 right-0 bottom-0 z-[60] w-[min(500px,100vw)] bg-surface border-l border-border shadow-2xl flex flex-col outline-none"
    >
      <header className="px-5 pt-4 pb-0 border-b border-border shrink-0">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="m-0 text-h3 text-gat-greenDarkest">Customize view</h3>
            <div className="text-[11.5px] text-text-muted mt-0.5">
              {view.active ? (
                <>
                  View: <b className="text-text-dark">{view.active.name}</b>
                  {readOnly ? ` · from ${view.active.ownerName}` : ''}
                </>
              ) : (
                <>
                  View: <b className="text-text-dark">Default</b>
                </>
              )}
              {view.modified && (
                <span className="ml-2 text-amber-700 font-semibold" data-testid="unsaved">
                  ● unsaved changes
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-[18px] leading-none text-text-body hover:bg-black/5 px-2 py-1 rounded"
          >
            ×
          </button>
        </div>
        <div role="tablist" aria-label="Customize sections" className="flex gap-1 mt-3 -mb-px overflow-x-auto" onKeyDown={onTabKey}>
          {TABS.map(([k, label]) => (
            <button
              key={k}
              ref={(el) => {
                tabRefs.current[k] = el;
              }}
              type="button"
              role="tab"
              aria-selected={tab === k}
              tabIndex={tab === k ? 0 : -1}
              onClick={() => setTab(k)}
              className={`px-3 py-2 text-[12px] font-semibold whitespace-nowrap border-b-2 ${tab === k ? 'border-gat-green text-gat-greenDark' : 'border-transparent text-text-muted hover:text-text-dark'}`}
            >
              {label}
            </button>
          ))}
        </div>
      </header>

      <div role="tabpanel" aria-label={TABS.find(([k]) => k === tab)[1]} className="flex-1 overflow-y-auto px-5 py-4 go-fade-in" key={tab}>
        {tab === 'columns' && <ColumnsSection view={view} defs={defs} />}
        {tab === 'highlights' && <HighlightsSection view={view} defs={defs} />}
        {tab === 'appearance' && <AppearanceSection view={view} />}
        {tab === 'rows' && <RowsSection view={view} defs={defs} />}
        {tab === 'groups' && view.tree && <GroupRowsSection view={view} hasGroups={hasGroups} />}
        {tab === 'sharing' && <SharingSection view={view} onShare={onShare} />}
      </div>

      <footer className="shrink-0 border-t border-border bg-surface-2 px-5 py-3 space-y-2">
        {naming != null && (
          <form onSubmit={saveNew} className="flex gap-2">
            <input
              autoFocus
              aria-label="New view name"
              placeholder="Name the new view"
              maxLength={60}
              value={naming}
              onChange={(e) => setNaming(e.target.value)}
              className="flex-1 border border-border-strong rounded px-2 py-1.5 text-[12px]"
            />
            <Button size="sm" type="submit" disabled={!naming.trim()}>
              Save
            </Button>
            <Button size="sm" variant="ghost" type="button" onClick={() => setNaming(null)}>
              Cancel
            </Button>
          </form>
        )}
        {view.error && (
          <div className="text-[11.5px] text-status-lost" role="alert">
            {view.error}
          </div>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          {view.enabled && view.modified && view.active && !readOnly && (
            <Button size="sm" onClick={() => view.update()}>
              Save
            </Button>
          )}
          {view.enabled && naming == null && (
            <Button size="sm" variant={view.modified && (!view.active || readOnly) ? 'primary' : 'secondary'} onClick={() => setNaming('')}>
              Save as new…
            </Button>
          )}
          {view.modified && (
            <Button size="sm" variant="ghost" onClick={view.revert}>
              Revert
            </Button>
          )}
          <button
            type="button"
            className="ml-auto text-[11.5px] font-semibold text-text-muted hover:text-status-lost hover:underline"
            onClick={view.resetAll}
            title="Every setting back to the built-in Default (your filters and search stay)"
          >
            Reset everything
          </button>
          <Button size="sm" variant="secondary" onClick={onClose}>
            Done
          </Button>
        </div>
      </footer>
    </aside>
  );
}
